import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import API_URL from '../config/api';

interface User {
  id: number | string;
  username?: string;
  profileId?: string;
  isAdmin: boolean;
  dni?: string;
  nombre?: string;
  apellido?: string;
  role?: string;
  hasCompletedTest?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Configurar axios con base URL
axios.defaults.baseURL = API_URL;

// Interceptor para agregar token a las requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar respuestas de error
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = React.useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const verifyToken = React.useCallback(async () => {
    try {
      const response = await axios.get('/auth/verify');
      if (response.data.valid) {
        const userData = response.data.user;
        console.log('Token verified, user:', userData);
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        console.log('Token verification failed: invalid response');
        logout();
      }
    } catch (error: any) {
      console.error('Token verification failed:', error);
      // Si falla la verificación, mantener el usuario en el estado (confiar en localStorage)
      // Solo eliminar si hay un error crítico
      if (error?.response?.status === 401) {
        console.log('Token invalid or expired, keeping user from localStorage');
        // No hacer logout, mantener el usuario
      } else {
        console.log('Unexpected error in token verification, keeping user');
        // Mantener el usuario en caso de otros errores también
      }
    }
  }, [logout]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        // Verificar token solo si el usuario tiene una estructura válida
        if (userData.id) {
          verifyToken();
        } else {
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, [verifyToken]);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Intentar login de administrador primero
      let response;
      try {
        response = await axios.post('/auth/admin/login', { username, password });
      } catch (adminError: any) {
        // Si falla el login de admin, intentar login de estudiante
        if (adminError.response?.status === 401) {
          response = await axios.post('/auth/login', { username, password });
        } else {
          throw adminError;
        }
      }
      
      if (response.data.success) {
        const { token, user: userData } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        
        // Redireccionar según el tipo de usuario
        if (userData.isAdmin) {
          window.location.href = '/admin/dashboard';
        } else {
          window.location.href = '/placement/test';
        }
        
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
